import { Box, Tooltip, makeStyles } from '@material-ui/core';
import { LockIcon } from '@setvi/shared/components/sicons';
import { getInitials } from 'Utils/getInitials';
import { RfqUserAvatar } from 'pages/auto-rfq/components/user-avatar';
import type { RfqMemberProfileData } from 'lib/realtime/RfqSpacesProvider';

const useStyles = makeStyles(() => ({
  // Lock glyph pinned to the start (left) of the input, vertically centered.
  // The host input reserves left padding so the value never sits under it.
  lock: {
    position: 'absolute',
    left: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 5,
    display: 'inline-flex',
    pointerEvents: 'none'
  },
  // Editor's avatar, overhanging the top-right corner of the input.
  avatarWrap: {
    position: 'absolute',
    top: -8,
    right: -8,
    zIndex: 6,
    display: 'inline-flex'
  },
  avatar: {
    border: '2px solid #FFFFFF',
    outline: '1.5px solid #1E96FC',
    fontSize: 10,
    fontWeight: 600,
    boxShadow: '0 1px 3px rgba(30,150,252,0.35)'
  }
}));

/** Lock glyph rendered at the start of a locked input. */
export const FieldLockIcon = () => {
  const classes = useStyles();
  return (
    <Box className={classes.lock}>
      <LockIcon style={{ width: 16, height: 16 }} htmlColor="#1E96FC" />
    </Box>
  );
};

interface FieldLockAvatarProps {
  holder: RfqMemberProfileData | null;
}

/**
 * Editor's avatar at the top-right corner of a locked input, tooltip naming who
 * holds the lock. Render inside a `position: relative` wrapper around the input.
 */
export const FieldLockAvatar = ({ holder }: FieldLockAvatarProps) => {
  const classes = useStyles();

  if (!holder) return null;

  const name = holder.name || 'Another user';
  const initials =
    getInitials({
      firstName: holder.firstName,
      lastName: holder.lastName,
      email: holder.email
    }) || (name.charAt(0) || '?').toUpperCase();

  return (
    <Tooltip arrow title={`${name} is editing`}>
      <Box className={classes.avatarWrap}>
        <RfqUserAvatar
          size="sm"
          userId={holder.userId}
          value={initials}
          className={classes.avatar}
        />
      </Box>
    </Tooltip>
  );
};
